// =====================================================================
// SecuriteProcedures.jsx — les procédures, réglées à l'écran (23-10-2026)
// =====================================================================
// Avant, chaque ligne, son ordre, sa fréquence ne changeaient que par un script.
// Demande de Silviu : « să poată să fie drepturi pe care să le alocăm noi adminii » —
// ce sont les admins qui donnent le droit « Modifier les procédures »
// (maintenance.configure.all) depuis l'écran Permissions.
// Chaque vérification : son texte (FR / RO / EN), sa fréquence (« la cât timp »),
// sa place dans l'ordre de la tournée, la tâche ZeusLog où va le temps passé et sa
// durée habituelle. Les tournées déjà faites ne changent pas (lignes figées) ; la
// tournée à venir suit tant que rien n'y a été fait.
// =====================================================================

import { useState, useEffect, useCallback } from 'react';
import { safetyRoutines, errMsg } from '../services/api.js';
import { FREQUENCES, erreurProcedure } from '../lib/tournee.js';

const C = { line: '#e5e7eb', ink: '#3f4654', mute: '#7b8494', brand: '#714B67', ok: '#16a34a', okBg: '#effaf3', crit: '#dc2626', critBg: '#fdf0f0' };
const card = { background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, padding: '1rem 1.1rem', marginBottom: '1rem' };
const inp = { width: '100%', padding: '0.5rem', border: `1px solid ${C.line}`, borderRadius: 6, fontSize: '0.93rem', boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: '0.72rem', fontWeight: 700, color: C.mute, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.04em' };
const btn = (bg, fg = '#fff') => ({
  background: bg, color: fg, border: bg === '#fff' ? `1px solid ${C.line}` : '1px solid transparent',
  borderRadius: 7, padding: '0.5rem 0.9rem', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
});
const VIDE = { label: '', labelRo: '', labelEn: '', periodicityWeeks: 1, projectId: '', taskId: '', standardMinutes: '', isActive: true };
const versServeur = (x) => ({
  label: x.label, labelRo: x.labelRo || null, labelEn: x.labelEn || null, helpText: x.helpText || null,
  periodicityWeeks: Number(x.periodicityWeeks), isActive: x.isActive !== false,
  projectId: x.projectId || null, taskId: x.taskId || null,
  standardMinutes: x.standardMinutes === '' || x.standardMinutes === null || x.standardMinutes === undefined ? null : Number(x.standardMinutes),
});

function Ligne({ it, autres, projets, onEnregistrer, onMonter, onDescendre, premier, dernier, periodiciteJours }) {
  const [x, setX] = useState(it);
  useEffect(() => setX(it), [it]);
  const erreur = erreurProcedure(x, autres);
  const modifiee = JSON.stringify(versServeur(x)) !== JSON.stringify(versServeur(it));
  const taches = projets.find((p) => p.id === x.projectId)?.tasks || [];
  const set = (k) => (e) => setX((v) => ({ ...v, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  return (
    <div style={{ borderTop: `1px solid ${C.line}`, padding: '10px 0', opacity: x.isActive ? 1 : 0.6 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <button type="button" disabled={premier} onClick={onMonter} title="Monter" style={{ ...btn('#fff', C.ink), padding: '2px 8px' }}>↑</button>
          <button type="button" disabled={dernier} onClick={onDescendre} title="Descendre" style={{ ...btn('#fff', C.ink), padding: '2px 8px' }}>↓</button>
        </div>
        <div style={{ flex: 1, display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl}>Vérification (français)</label>
            <input style={inp} value={x.label} onChange={set('label')} />
          </div>
          <div><label style={lbl}>Roumain</label><input style={inp} value={x.labelRo || ''} onChange={set('labelRo')} /></div>
          <div><label style={lbl}>Anglais</label><input style={inp} value={x.labelEn || ''} onChange={set('labelEn')} /></div>
          <div>
            <label style={lbl}>À faire</label>
            {/* Une routine qui ne tourne pas chaque semaine (machines : 14 ou 90 jours) a son propre rythme :
                chaque vérification se fait à chaque passage — un choix « chaque semaine » y serait faux. */}
            {periodiciteJours === 7 ? (
              <select style={inp} value={x.periodicityWeeks} onChange={set('periodicityWeeks')}>
                {FREQUENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            ) : (
              <div style={{ ...inp, background: '#f8fafc', color: C.ink }}>à chaque passage — tous les {periodiciteJours} jours</div>
            )}
          </div>
          <div>
            <label style={lbl}>Le temps va au projet</label>
            <select style={inp} value={x.projectId || ''} onChange={(e) => setX((v) => ({ ...v, projectId: e.target.value, taskId: '' }))}>
              <option value="">— aucun —</option>
              {projets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>… et à la tâche</label>
            <select style={inp} value={x.taskId || ''} onChange={set('taskId')} disabled={!x.projectId}>
              <option value="">{x.projectId ? '— choisir —' : '— d’abord le projet —'}</option>
              {taches.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Durée habituelle (min)</label>
            <input style={inp} type="number" min="1" max="240" value={x.standardMinutes ?? ''} onChange={set('standardMinutes')}
              placeholder="si le chrono est oublié" />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem' }}>
              <input type="checkbox" checked={x.isActive !== false} onChange={set('isActive')} /> Active
            </label>
            <span style={{ fontSize: '0.8rem', color: C.mute }}>
              {it.derniereFois ? `Dernière fois : ${String(it.derniereFois).slice(0, 10)}` : 'Jamais faite'}
            </span>
          </div>
        </div>
      </div>
      {(modifiee || erreur) && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'flex-end', marginTop: 8, flexWrap: 'wrap' }}>
          {erreur && <span style={{ color: C.crit, fontSize: '0.85rem', fontWeight: 600 }}>{erreur}</span>}
          {modifiee && (
            <>
              <button type="button" style={btn('#fff', C.mute)} onClick={() => setX(it)}>Annuler</button>
              <button type="button" disabled={Boolean(erreur)} style={btn(erreur ? '#cbd5e1' : C.brand)}
                onClick={() => onEnregistrer(versServeur(x))}>Enregistrer</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function SecuriteProcedures({ onAllerTournee }) {
  const [data, setData] = useState(null);
  const [nouvelle, setNouvelle] = useState({});   // templateId → brouillon
  const [err, setErr] = useState(null);
  const [flash, setFlash] = useState(null);

  const charger = useCallback(async () => {
    try { setData(await safetyRoutines.procedures()); } catch (e) { setErr(errMsg(e)); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const agir = async (fn, ok) => {
    setErr(null); setFlash(null);
    try { await fn(); setFlash(ok); await charger(); return true; } catch (e) { setErr(errMsg(e)); return false; }
  };
  const deplacer = (p, i, dir) => {
    const ids = p.items.map((x) => x.id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    agir(() => safetyRoutines.reorder(p.id, ids), 'Ordre enregistré.');
  };

  if (!data) return err ? <div style={{ ...card, background: C.critBg, color: '#991B1B' }}>{err}</div> : null;
  return (
    <div>
      <p style={{ margin: '0 0 0.8rem', color: C.mute, fontSize: '0.88rem' }}>
        Les tournées déjà faites ne changent pas. La tournée à venir suit vos modifications tant que personne n’y a encore répondu.
      </p>
      {/* « Je ne vois pas où il attribue les personnes » (Silviu, 23-10-2026) : ici se règle QUOI vérifier ;
          QUI le fait se choisit à chaque tournée, dans l'onglet « Tournée ». */}
      <div style={{ ...card, background: '#eef2ff', borderColor: '#c7d2fe', display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <span style={{ color: '#312e81', fontSize: '0.92rem' }}>
          <b>Qui fait quoi ?</b> Les personnes se choisissent pour chaque tournée, dans l’onglet « Tournée » — la tournée suivante reprend la même répartition.
        </span>
        {onAllerTournee && (
          <button type="button" style={btn('#4338ca')} onClick={onAllerTournee}>Répartir les personnes →</button>
        )}
      </div>
      {err && <div style={{ ...card, background: C.critBg, borderColor: '#f6cfcf', color: '#991B1B' }}>{err}</div>}
      {flash && <div style={{ ...card, background: C.okBg, borderColor: '#cfead9', color: '#065F46', fontWeight: 600 }}>{flash}</div>}
      {data.procedures.map((p) => {
        const n = nouvelle[p.id] || VIDE;
        const errN = n.label ? erreurProcedure(n, p.items) : null;
        return (
          <div key={p.id} style={card}>
            <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>{p.name}</div>
            <div style={{ fontSize: '0.82rem', color: C.mute, marginBottom: 4 }}>{p.items.length} vérification(s) — dans l’ordre de la tournée</div>
            {p.items.map((it, i) => (
              <Ligne key={it.id} it={it} autres={p.items} projets={data.projets}
                premier={i === 0} dernier={i === p.items.length - 1} periodiciteJours={p.periodicityDays}
                onMonter={() => deplacer(p, i, -1)} onDescendre={() => deplacer(p, i, +1)}
                onEnregistrer={(v) => agir(() => safetyRoutines.updateItem(it.id, v), `« ${v.label} » enregistrée.`)} />
            ))}
            <div style={{ borderTop: `2px dashed ${C.line}`, marginTop: 8, paddingTop: 10 }}>
              <label style={lbl}>Ajouter une vérification</label>
              <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
                <input style={{ ...inp, gridColumn: '1 / -1' }} value={n.label} placeholder="Ex. Extincteurs : présents, accessibles, plombés"
                  onChange={(e) => setNouvelle((v) => ({ ...v, [p.id]: { ...n, label: e.target.value } }))} />
                {p.periodicityDays === 7 && (
                  <select style={inp} value={n.periodicityWeeks} onChange={(e) => setNouvelle((v) => ({ ...v, [p.id]: { ...n, periodicityWeeks: e.target.value } }))}>
                    {FREQUENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                )}
                <select style={inp} value={n.projectId} onChange={(e) => setNouvelle((v) => ({ ...v, [p.id]: { ...n, projectId: e.target.value, taskId: '' } }))}>
                  <option value="">— projet du temps —</option>
                  {data.projets.map((pr) => <option key={pr.id} value={pr.id}>{pr.name}</option>)}
                </select>
                <select style={inp} value={n.taskId} disabled={!n.projectId} onChange={(e) => setNouvelle((v) => ({ ...v, [p.id]: { ...n, taskId: e.target.value } }))}>
                  <option value="">{n.projectId ? '— tâche —' : '— d’abord le projet —'}</option>
                  {(data.projets.find((pr) => pr.id === n.projectId)?.tasks || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'flex-end', marginTop: 8 }}>
                {errN && <span style={{ color: C.crit, fontSize: '0.85rem', fontWeight: 600 }}>{errN}</span>}
                <button type="button" disabled={!n.label || Boolean(errN)} style={btn(!n.label || errN ? '#cbd5e1' : C.brand)}
                  onClick={async () => {
                    if (await agir(() => safetyRoutines.createItem(p.id, versServeur(n)), `« ${n.label} » ajoutée.`)) {
                      setNouvelle((v) => ({ ...v, [p.id]: VIDE }));
                    }
                  }}>Ajouter</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
